import { Button } from '~/components/common/Button'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import { LicenseInstance } from '~/services/superAdminService'
import { isUnlimited } from '~/utils/quotaConstants'
import { formatDate } from '~/utils/timezone'
import { LicenseMetadataDetails } from './LicenseMetadataDetails'

interface LicenseDetailsModalProps {
  isOpen: boolean
  selectedLicenseDetails: LicenseInstance | null
  onClose: () => void
}

export const LicenseDetailsModal = ({
  isOpen,
  selectedLicenseDetails,
  onClose
}: LicenseDetailsModalProps) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    hideCloseButton={false}
    className='w-[600px] max-h-[80vh]'
    overlayClassName='z-[60]'
  >
    <ModalBody className='overflow-y-auto'>
      <h3 className='text-lg font-semibold mb-4'>
        License Details - {selectedLicenseDetails?.license_type_label}
      </h3>
      {selectedLicenseDetails && (
        <div className='space-y-4 text-sm'>
          <div className='border rounded-lg p-3'>
            <h4 className='font-semibold text-gray-700 mb-2'>Basic Information</h4>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <span className='text-gray-500'>ID:</span>{' '}
                <span className='font-mono'>{selectedLicenseDetails.id}</span>
              </div>
              <div>
                <span className='text-gray-500'>Type:</span>{' '}
                {selectedLicenseDetails.license_type_label}
              </div>
              <div>
                <span className='text-gray-500'>Status:</span>{' '}
                <span
                  className={`capitalize font-semibold ${
                    selectedLicenseDetails.status === 'active'
                      ? 'text-green-600'
                      : selectedLicenseDetails.status === 'pending'
                        ? 'text-yellow-600'
                        : 'text-gray-600'
                  }`}
                >
                  {selectedLicenseDetails.status}
                </span>
              </div>
              <div>
                <span className='text-gray-500'>Billing Period:</span>{' '}
                {selectedLicenseDetails.is_billing_period ? '✅ Yes' : '❌ No'}
              </div>
              <div>
                <span className='text-gray-500'>Billing Type:</span>{' '}
                {selectedLicenseDetails.billing_type}
              </div>
            </div>
          </div>

          <div className='border rounded-lg p-3'>
            <h4 className='font-semibold text-gray-700 mb-2'>Dates</h4>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <span className='text-gray-500'>Start:</span>{' '}
                {formatDate(selectedLicenseDetails.start_date)}
              </div>
              <div>
                <span className='text-gray-500'>End:</span>{' '}
                {formatDate(selectedLicenseDetails.end_date)}
              </div>
              {selectedLicenseDetails.contract_end_date && (
                <div className='col-span-2'>
                  <span className='text-gray-500'>Contract End:</span>{' '}
                  <span className='text-red-600'>
                    {formatDate(selectedLicenseDetails.contract_end_date)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className='border rounded-lg p-3'>
            <h4 className='font-semibold text-gray-700 mb-2'>Quota & Credits</h4>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <span className='text-gray-500'>Credit Quota:</span>{' '}
                {selectedLicenseDetails.billing_type === 'billable_flow' ? (
                  <span className='text-gray-400'>N/A</span>
                ) : isUnlimited(selectedLicenseDetails.quota) ? (
                  'Unlimited'
                ) : (
                  (selectedLicenseDetails.quota?.toLocaleString() ?? 'N/A')
                )}
              </div>
              <div>
                <span className='text-gray-500'>Billable Flows Quota:</span>{' '}
                {selectedLicenseDetails.billing_type !== 'billable_flow' ? (
                  <span className='text-gray-400'>N/A</span>
                ) : isUnlimited(selectedLicenseDetails.billable_flows_quota) ? (
                  'Unlimited'
                ) : (
                  (selectedLicenseDetails.billable_flows_quota?.toLocaleString() ?? 'N/A')
                )}
              </div>
              {selectedLicenseDetails.carried_over_credits !== null &&
                selectedLicenseDetails.carried_over_credits > 0 && (
                  <div className='col-span-2'>
                    <span className='text-gray-500'>Carried Over Credits:</span>{' '}
                    <span className='text-blue-600 font-semibold'>
                      +{selectedLicenseDetails.carried_over_credits.toLocaleString()}
                    </span>
                    <span className='text-gray-400 text-xs ml-2'>(from upgrade proration)</span>
                  </div>
                )}
            </div>
          </div>

          {selectedLicenseDetails.subscription_id && (
            <div className='border rounded-lg p-3'>
              <h4 className='font-semibold text-gray-700 mb-2'>Stripe</h4>
              <div className='grid grid-cols-2 gap-2'>
                <div className='col-span-2'>
                  <span className='text-gray-500'>Subscription ID:</span>{' '}
                  <span className='font-mono text-xs'>
                    {selectedLicenseDetails.subscription_id}
                  </span>
                </div>
                <div>
                  <span className='text-gray-500'>Auto Renew:</span>{' '}
                  {selectedLicenseDetails.auto_renew ? '✅ Yes' : '❌ No'}
                </div>
                {selectedLicenseDetails.stripe_customer_id && (
                  <div>
                    <span className='text-gray-500'>Customer ID:</span>{' '}
                    <span className='font-mono text-xs'>
                      {selectedLicenseDetails.stripe_customer_id}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedLicenseDetails.billing_model === 'custom' && (
            <div className='border rounded-lg p-3'>
              <h4 className='font-semibold text-gray-700 mb-2'>Enterprise Contract</h4>
              <div className='grid grid-cols-2 gap-2'>
                {selectedLicenseDetails.contract_value_cents && (
                  <div>
                    <span className='text-gray-500'>Contract Value:</span> $
                    {(selectedLicenseDetails.contract_value_cents / 100).toLocaleString()}
                  </div>
                )}
                {selectedLicenseDetails.billing_schedule && (
                  <div>
                    <span className='text-gray-500'>Billing Schedule:</span>{' '}
                    <span className='capitalize'>{selectedLicenseDetails.billing_schedule}</span>
                  </div>
                )}
                {selectedLicenseDetails.payment_terms_days && (
                  <div>
                    <span className='text-gray-500'>Payment Terms:</span> Net{' '}
                    {selectedLicenseDetails.payment_terms_days}
                  </div>
                )}
                {selectedLicenseDetails.total_invoiced_cents !== null && (
                  <div>
                    <span className='text-gray-500'>Total Invoiced:</span> $
                    {((selectedLicenseDetails.total_invoiced_cents ?? 0) / 100).toLocaleString()}
                  </div>
                )}
                {selectedLicenseDetails.total_paid_cents !== null && (
                  <div>
                    <span className='text-gray-500'>Total Paid:</span>{' '}
                    <span className='text-green-600'>
                      ${((selectedLicenseDetails.total_paid_cents ?? 0) / 100).toLocaleString()}
                    </span>
                  </div>
                )}
                {selectedLicenseDetails.invoices_created &&
                  selectedLicenseDetails.invoices_created.length > 0 && (
                    <div className='col-span-2'>
                      <span className='text-gray-500'>Invoices:</span>{' '}
                      {selectedLicenseDetails.invoices_created.length}
                    </div>
                  )}
              </div>
            </div>
          )}

          <LicenseMetadataDetails selectedLicenseDetails={selectedLicenseDetails} />
        </div>
      )}
    </ModalBody>
    <ModalFooter>
      <Button variant='outlined' onClick={onClose}>
        Close
      </Button>
    </ModalFooter>
  </Modal>
)
